import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, weekStart, type LoadLevel } from '@pz/core';
import { HoursInput } from './entry';
import { Button, IconButton } from './ui';
import { kw } from '../lib/format';
import { useMe } from '../lib/me';

/** Shared parts of Auslastung and Planung. */

export const LEVEL_BG: Record<LoadLevel, string> = {
  none: 'hatch',
  low: 'bg-info-soft',
  ok: 'bg-good-soft',
  high: 'bg-warn-soft',
  over: 'bg-bad-soft',
};

export function useWeekRange(weeks = 10) {
  const { me } = useMe();
  const [start, setStart] = useState(addDays(weekStart(me.today), -14));
  return {
    from: start,
    to: addDays(start, weeks * 7 - 1),
    prev: () => setStart(addDays(start, -28)),
    next: () => setStart(addDays(start, 28)),
    today: () => setStart(addDays(weekStart(me.today), -14)),
  };
}

export function RangeNav({ range }: { range: ReturnType<typeof useWeekRange> }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1">
      <IconButton label={t('common.previous')} icon={<ChevronLeft className="size-4" />} onClick={range.prev} />
      <span className="num text-sm">
        {t('common.cw')} {kw(range.from)} - {kw(range.to)}
      </span>
      <IconButton label={t('common.next')} icon={<ChevronRight className="size-4" />} onClick={range.next} />
      <Button size="sm" variant="ghost" onClick={range.today}>
        {t('common.today')}
      </Button>
    </div>
  );
}

export function CellInput({ value, onSave }: { value: number; onSave: (m: number | null) => void }) {
  const [v, setV] = useState<number | null>(value || null);
  useEffect(() => setV(value || null), [value]);
  return (
    <div
      onBlur={() => {
        if ((v ?? 0) !== value) onSave(v);
      }}
      className="[&_input]:h-7 [&_input]:w-16 [&_input]:px-1 [&_input]:text-center"
    >
      <HoursInput value={v} onChange={setV} />
    </div>
  );
}

